import { Flex } from 'antd';
import { Paragraph, Text } from '../Ant';
import CollapsibleDateGroup from '../collapsibleDateGroup/collapsibleDateGroup';
import {
  DateGroupList,
  toDateGroupKey,
  useCollapsedDateGroups,
} from '../../hooks/useCollapsedDateGroups';
import WorktimeCard from './worktimeCard';
import { IGroupedWorkTimes } from '../../interface/workTimes';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { getWorkTimeDuration } from '../../constants/workTime';
import NoData from '../dashboard/noData';
import { WorktimeEmpty } from '../icons';
import { AgendaTimeWrapper } from '../../assets/styles/components/worktime.styled';
import { formatDateGroupTitle } from '../../constants/utilityFunctions';
import SortSelector from '../sortPopover/sortSelector';
import { IActiveSortState, ISortOption } from '../../interface/common';
import SkeletonGroupedCardList from '../common/skeleton/skeletonGroupedCardList';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';

interface IWorkTimeAgendaView {
  groupedWorkTimes: IGroupedWorkTimes[];
  isLoading?: boolean;
  calendarActiveSort: IActiveSortState;
  calendarSortOptions: ISortOption[];
}
const AgendaView = ({
  groupedWorkTimes,
  isLoading,
  calendarActiveSort,
  calendarSortOptions,
}: IWorkTimeAgendaView) => {
  const { t } = useTranslation();
  const { isCollapsed, toggle } = useCollapsedDateGroups(DateGroupList.worktimeAgenda);
  return (
    <CollapsibleListLayout
      testId="agenda-view"
      contentGap="gutter"
      ownsTopGutter={false}
      header={
        <SortSelector
          modalLabel={t('common:sorting.title')}
          options={calendarSortOptions}
          activeSort={calendarActiveSort}
          disabled
        />
      }
    >
      {groupedWorkTimes?.map((workTime) => {
        const groupKey = toDateGroupKey(workTime?.date);
        return (
          <CollapsibleDateGroup
            key={groupKey}
            title={formatDateGroupTitle(workTime?.date, t)}
            itemCount={t('worktime:workTimeCount', { count: workTime?.workTimes?.length })}
            total={`${workTime?.totalDuration?.value ?? 0} ${workTime?.totalDuration.unit === 'minutes' ? 'm' : 'h'}`}
            collapsed={isCollapsed(groupKey)}
            onToggle={() => toggle(groupKey)}
          >
            {workTime?.workTimes.map((item) => {
              return (
                <Flex gap={16} align="center" key={item?.workTime?.id}>
                  <AgendaTimeWrapper vertical>
                    <Paragraph
                      $fontsize="13px"
                      $lineheight="20px"
                      $color="title"
                      $weight={500}
                      $width="max-content"
                    >
                      {dayjs(item?.workTime?.startTime).format('HH:mm')}
                    </Paragraph>
                    <Text
                      $color="description"
                      $fontsize="12px"
                      $lineheight="20px"
                      $width="max-content"
                    >
                      {getWorkTimeDuration(item?.workTime?.startTime, item?.workTime?.endTime)}
                    </Text>
                  </AgendaTimeWrapper>
                  <WorktimeCard
                    title={item?.workTime?.subject}
                    id={item?.workTime?.id}
                    key={item?.workTime?.id}
                    color={item?.workTime?.workTimeType?.color}
                    currentOfTotalDays={item?.currentOfTotalDays}
                    description={
                      item?.workTime?.matter &&
                      `${item?.workTime?.matter?.matterNumber} - ${item?.workTime?.matter?.subject}`
                    }
                  />
                </Flex>
              );
            })}
          </CollapsibleDateGroup>
        );
      })}
      {isLoading && (
        <SkeletonGroupedCardList rows={[1]} withSubcontentOnFirstRow withHeader={false} />
      )}
      {(!groupedWorkTimes || (Object.entries(groupedWorkTimes).length === 0 && !isLoading)) && (
        <div className="flex-grow d-flex justify-center items-center">
          <NoData header={t('worktime:noWorkTimes')} icon={<WorktimeEmpty />} />
        </div>
      )}
    </CollapsibleListLayout>
  );
};

export default AgendaView;
